import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { useLanguage } from '../context/LanguageContext';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Mail, Lock, ArrowLeft, CheckCircle, Loader2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Page 1: Request password reset (enter email)
export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast({ 
        title: language === 'nl' ? 'Vul je email in' : 'Enter your email', 
        variant: 'destructive' 
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });
      
      const data = await response.json();
      setEmailSent(true);
      
    } catch (error) {
      console.error('Error:', error);
      toast({ 
        title: language === 'nl' ? 'Er ging iets mis' : 'Something went wrong', 
        variant: 'destructive' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F7] flex flex-col">
      <Toaster />
      <Header />
      
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md shadow-xl rounded-3xl border-0">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <SwipeMatchLogo variant="color" size="default" className="mx-auto mb-6" />
              
              {emailSent ? (
                <>
                  <div className="w-16 h-16 rounded-full bg-[#2E7D32]/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-[#2E7D32]" />
                  </div>
                  <h1 className="text-2xl font-black text-[#2A2A2A] mb-2">
                    {language === 'nl' ? 'Check je inbox!' : 'Check your inbox!'}
                  </h1>
                  <p className="text-[#9E9E9E]">
                    {language === 'nl' 
                      ? `We hebben een reset link gestuurd naar ${email}`
                      : `We've sent a reset link to ${email}`}
                  </p>
                </>
              ) : (
                <>
                  <h1 className="text-2xl font-black text-[#2A2A2A] mb-2">
                    {language === 'nl' ? 'Wachtwoord vergeten?' : 'Forgot password?'}
                  </h1>
                  <p className="text-[#9E9E9E]">
                    {language === 'nl' 
                      ? 'Vul je email in en we sturen je een reset link'
                      : 'Enter your email and we\'ll send you a reset link'}
                  </p>
                </>
              )}
            </div>

            {emailSent ? (
              <div className="space-y-4">
                <p className="text-sm text-center text-[#9E9E9E]">
                  {language === 'nl' 
                    ? 'Geen email ontvangen? Check je spam folder of probeer opnieuw.'
                    : 'No email received? Check your spam folder or try again.'}
                </p>
                <Button 
                  onClick={() => setEmailSent(false)}
                  variant="outline"
                  className="w-full h-12 rounded-xl"
                >
                  {language === 'nl' ? 'Probeer opnieuw' : 'Try again'}
                </Button>
                <Link to="/login">
                  <Button 
                    variant="ghost"
                    className="w-full h-12 rounded-xl text-[#2E7D32]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {language === 'nl' ? 'Terug naar inloggen' : 'Back to login'}
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#2A2A2A]">E-mail</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9E9E9E]" />
                    <Input
                      type="email"
                      placeholder="jouw@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 pl-12 rounded-xl border-[#E5E5E5] focus:ring-[#2E7D32]"
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-[#2E7D32] hover:bg-[#256B2B] text-white font-semibold rounded-xl" 
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    language === 'nl' ? 'Verstuur reset link' : 'Send reset link'
                  )}
                </Button>

                <Link to="/login">
                  <Button 
                    type="button"
                    variant="ghost"
                    className="w-full h-12 rounded-xl text-[#9E9E9E] hover:text-[#2A2A2A]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {language === 'nl' ? 'Terug naar inloggen' : 'Back to login'}
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
};

// Page 2: Reset password (enter new password)
export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { toast } = useToast();
  const { language } = useLanguage();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/verify-reset-token/${token}`);
        const data = await response.json();
        
        setTokenValid(data.valid);
        if (data.email) {
          setUserEmail(data.email);
        }
      } catch (error) {
        console.error('Error verifying token:', error);
        setTokenValid(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({ 
        title: language === 'nl' ? 'Wachtwoorden komen niet overeen' : 'Passwords do not match', 
        variant: 'destructive' 
      });
      return;
    }

    if (password.length < 8) {
      toast({ 
        title: language === 'nl' ? 'Wachtwoord moet minimaal 8 karakters zijn' : 'Password must be at least 8 characters', 
        variant: 'destructive' 
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Reset failed');
      }
      
      setResetSuccess(true);
      
    } catch (error) {
      console.error('Error:', error);
      toast({ 
        title: error.message || (language === 'nl' ? 'Er ging iets mis' : 'Something went wrong'), 
        variant: 'destructive' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-[#F7F7F7] flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#2E7D32]" />
        </main>
        <Footer />
      </div>
    );
  }

  // Invalid token
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-[#F7F7F7] flex flex-col">
        <Toaster />
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md shadow-xl rounded-3xl border-0">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-black text-[#2A2A2A] mb-2">
                {language === 'nl' ? 'Ongeldige link' : 'Invalid link'}
              </h1>
              <p className="text-[#9E9E9E] mb-6">
                {language === 'nl' 
                  ? 'Deze reset link is ongeldig of verlopen. Vraag een nieuwe aan.'
                  : 'This reset link is invalid or expired. Request a new one.'}
              </p>
              <Link to="/wachtwoord-vergeten">
                <Button className="w-full h-12 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-xl">
                  {language === 'nl' ? 'Nieuwe link aanvragen' : 'Request new link'}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Success state
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-[#F7F7F7] flex flex-col">
        <Toaster />
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md shadow-xl rounded-3xl border-0">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[#2E7D32]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#2E7D32]" />
              </div>
              <h1 className="text-2xl font-black text-[#2A2A2A] mb-2">
                {language === 'nl' ? 'Wachtwoord gewijzigd!' : 'Password changed!'}
              </h1>
              <p className="text-[#9E9E9E] mb-6">
                {language === 'nl' 
                  ? 'Je wachtwoord is succesvol gewijzigd. Je kunt nu inloggen.'
                  : 'Your password has been changed successfully. You can now log in.'}
              </p>
              <Link to="/login">
                <Button className="w-full h-12 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-xl">
                  {language === 'nl' ? 'Naar inloggen' : 'Go to login'}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen bg-[#F7F7F7] flex flex-col">
      <Toaster />
      <Header />
      
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md shadow-xl rounded-3xl border-0">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <SwipeMatchLogo variant="color" size="default" className="mx-auto mb-6" />
              <h1 className="text-2xl font-black text-[#2A2A2A] mb-2">
                {language === 'nl' ? 'Nieuw wachtwoord' : 'New password'}
              </h1>
              <p className="text-[#9E9E9E]">
                {language === 'nl' 
                  ? `Kies een nieuw wachtwoord voor ${userEmail}`
                  : `Choose a new password for ${userEmail}`}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2A2A2A]">
                  {language === 'nl' ? 'Nieuw wachtwoord' : 'New password'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9E9E9E]" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 pl-12 rounded-xl border-[#E5E5E5] focus:ring-[#2E7D32]"
                    required
                    minLength={8}
                  />
                </div>
                <p className="text-xs text-[#9E9E9E]">
                  {language === 'nl' ? 'Minimaal 8 karakters' : 'Minimum 8 characters'}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2A2A2A]">
                  {language === 'nl' ? 'Bevestig wachtwoord' : 'Confirm password'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9E9E9E]" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-12 pl-12 rounded-xl border-[#E5E5E5] focus:ring-[#2E7D32]"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showPassword"
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                  className="rounded border-[#E5E5E5]"
                />
                <label htmlFor="showPassword" className="text-sm text-[#9E9E9E]">
                  {language === 'nl' ? 'Wachtwoord tonen' : 'Show password'}
                </label>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-[#2E7D32] hover:bg-[#256B2B] text-white font-semibold rounded-xl" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  language === 'nl' ? 'Wachtwoord wijzigen' : 'Change password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;
